import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { getCheckoutStatus, formatPrice } from "@/lib/api";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 8;

export default function CheckoutSuccess() {
  const { t } = useLang();
  const { clear } = useCart();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "", amount: 0, currency: "eur", items: [] });
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing", amount: 0, currency: "eur", items: [] });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setState({
          status: res.status,
          payment_status: res.payment_status,
          amount: (res.amount_total || 0) / 100,
          currency: res.currency || "eur",
          items: res.items || [],
        });
        if (res.payment_status === "paid") {
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.status === "expired") return;
        if (attempts.current < MAX_ATTEMPTS) {
          attempts.current += 1;
          setTimeout(poll, POLL_INTERVAL);
        } else {
          setState((s) => ({ ...s, status: "timeout" }));
        }
      } catch (e) {
        if (cancelled) return;
        if (attempts.current < MAX_ATTEMPTS) {
          attempts.current += 1;
          setTimeout(poll, POLL_INTERVAL);
        } else {
          setState({ status: "error", payment_status: "error", amount: 0, currency: "eur", items: [] });
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  const paid = state.payment_status === "paid";
  const checking = state.status === "checking" || (!paid && state.status !== "error" && state.status !== "timeout" && state.status !== "expired");

  return (
    <div data-testid="checkout-success-page" className="bg-maly-cream min-h-screen pt-32 lg:pt-40 pb-24 px-6 lg:px-12">
      <div className="max-w-[900px] mx-auto text-center">
        {checking && (
          <div className="py-24" data-testid="checkout-loading">
            <Loader2 size={36} strokeWidth={1.4} className="mx-auto animate-spin text-maly-navy/60 mb-8" />
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-4">MALY</div>
            <h1 className="font-display text-3xl lg:text-4xl font-light">{t.common.paymentPending}</h1>
          </div>
        )}

        {paid && (
          <div data-testid="checkout-success">
            <CheckCircle size={48} strokeWidth={1.2} className="mx-auto text-maly-sage mb-10" />
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-6">MALY · Order confirmed</div>
            <h1 className="font-display text-5xl lg:text-7xl font-light tracking-[-0.02em] leading-[1.05]">{t.common.paymentSuccess}</h1>
            <p className="font-serif italic text-lg lg:text-xl opacity-70 mt-8 max-w-lg mx-auto leading-relaxed">{t.common.paymentSuccessText}</p>

            {state.items?.length > 0 && (
              <div className="mt-14 bg-white border border-maly-navy/10 p-8 lg:p-12 text-left max-w-xl mx-auto">
                <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-60 mb-6">Order summary</div>
                <ul className="space-y-4">
                  {state.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-sm font-body">
                      <span>{it.name} {it.size && `· ${it.size}`} {it.color && `· ${it.color}`}</span>
                      <span>{it.quantity} × {formatPrice(it.unit_price, state.currency.toUpperCase())}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-maly-navy/10 mt-6 pt-6 flex justify-between font-display text-xl">
                  <span>{t.common.total}</span>
                  <span>{formatPrice(state.amount, state.currency.toUpperCase())}</span>
                </div>
              </div>
            )}

            <Link to="/shop" data-testid="success-back-shop" className="btn-primary mt-14">
              {t.common.backToShop} <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {(state.status === "error" || state.status === "expired" || state.status === "timeout") && !paid && !checking && (
          <div data-testid="checkout-error" className="py-16">
            <h1 className="font-display text-4xl lg:text-5xl font-light">{t.common.paymentFailed}</h1>
            <p className="font-body opacity-70 mt-6">{state.payment_status === "missing" ? "No session_id" : `Status: ${state.status}`}</p>
            <Link to="/cart" className="btn-primary mt-10 inline-flex">
              Back to cart
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
